<script setup lang="ts">
import { useAbility } from "@casl/vue";
import { computed, ref, watch } from "vue";
import { useNotification } from "@/composables/models/notification/useNotification";
import NavbarUserMenu from "./NavbarUserMenu.vue";
import NotificationMenu from "@/components/models/message/notification/NotificationMenu.vue";

const props = defineProps<{
  compacted: boolean;
  drawerOpen: boolean;
  isAdminPage?: boolean;
  itemSize: number;
  lgAndUp: boolean;
  mdAndDown: boolean;
  mobileActionBtnColor: string;
}>();

const emit = defineEmits<{
  (event: "toggle-drawer"): void;
  (event: "open-tutorial"): void;
  (event: "close-drawer"): void;
}>();

const { can } = useAbility();
const { unreadNotificationsCount } = useNotification();

const notificationBtnSize = computed(() => {
  if (props.mdAndDown) return props.itemSize;
  return props.compacted ? 34 : 40;
});

const notificationMenuOpen = ref(false);

watch(notificationMenuOpen, (open) => {
  if (open && props.drawerOpen) {
    emit("close-drawer");
  }
});

const toggleDrawer = () => emit("toggle-drawer");
</script>

<template>
  <div class="tw:flex tw:items-center">
    <NavbarUserMenu
      v-if="lgAndUp"
      :compacted="compacted"
      @open-tutorial="emit('open-tutorial')"
    />

    <div
      v-if="!isAdminPage"
      class="tw:flex tw:items-center tw:gap-0"
    >
      <v-menu
        v-if="can($permActions.READ, $permSubjects.MESSAGE__NOTIFICATION)"
        v-model="notificationMenuOpen"
        :close-on-content-click="false"
        location="bottom end"
      >
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            icon
            aria-label="Notifications"
            data-testid="notifications-link"
            data-tour="dashboard-notifications"
            :class="[
              'tw:!text-on-surface/80 tw:!transition-all tw:!duration-500',
              mdAndDown
                ? 'tw:!rounded-e-none tw:!rounded-s-xl'
                : 'tw:!rounded-[10px] tw:[--v-hover-opacity:0] tw:hover:!bg-background',
            ]"
            :color="mdAndDown ? mobileActionBtnColor : 'transparent'"
            :size="notificationBtnSize"
            variant="flat"
          >
            <div class="tw:relative tw:flex tw:items-center tw:justify-center">
              <v-icon
                class="tw:!transition-all tw:!duration-500"
                icon="mdi-bell-outline"
                :size="compacted ? 'small' : 'default'"
              />

              <span
                v-if="unreadNotificationsCount > 0"
                class="tw:absolute tw:-top-1 tw:-end-1 tw:z-floating-badge tw:size-2.5 tw:rounded-full tw:bg-error tw:ring-2 tw:ring-surface"
                data-testid="notifications-unread-dot"
              />
            </div>
          </v-btn>
        </template>

        <NotificationMenu @close="notificationMenuOpen = false" />
      </v-menu>

      <v-btn
        v-if="mdAndDown"
        :active="false"
        aria-label="Open navigation"
        :class="[
          'tw:min-w-auto tw:!text-on-surface/80 tw:!transition-all tw:!duration-500',
          can($permActions.READ, $permSubjects.MESSAGE__NOTIFICATION)
            ? 'tw:!rounded-e-md tw:!rounded-s-none'
            : 'tw:!rounded-md',
        ]"
        :color="mobileActionBtnColor"
        data-testid="nav-drawer-toggle"
        icon
        :size="itemSize"
        variant="flat"
        @click="toggleDrawer"
      >
        <v-icon
          class="tw:!transition-all tw:!duration-500"
          :icon="drawerOpen ? 'mdi-close' : 'mdi-dots-grid'"
          :size="compacted ? 'small' : 'default'"
        />
      </v-btn>
    </div>

    <div
      v-else
      class="tw:flex tw:items-center tw:!rounded-md"
      :class="{ 'tw:!rounded-e-none': !mdAndDown }"
    >
      <router-link
        v-if="mdAndDown"
        aria-label="Exit admin settings"
        data-testid="exit-admin-settings-link"
        to="/"
      >
        <v-btn
          icon
          class="tw:!rounded-e-none tw:!rounded-s-md tw:!text-on-surface/80 tw:!transition-all tw:!duration-500"
          :color="mobileActionBtnColor"
          :size="itemSize"
          variant="flat"
        >
          <v-icon
            class="tw:!transition-all tw:!duration-500"
            icon="mdi-arrow-left"
            :size="compacted ? 'small' : 'default'"
          />
        </v-btn>
      </router-link>

      <v-btn
        v-if="mdAndDown"
        :active="false"
        aria-label="Open navigation"
        class="tw:min-w-auto tw:!rounded-e-md tw:!rounded-s-none tw:!text-on-surface/80 tw:!transition-all tw:!duration-500"
        :color="mobileActionBtnColor"
        icon
        :size="itemSize"
        variant="flat"
        @click="toggleDrawer"
      >
        <v-icon
          class="tw:!transition-all tw:!duration-500"
          :icon="drawerOpen ? 'mdi-close' : 'mdi-dots-grid'"
          :size="compacted ? 'small' : 'default'"
        />
      </v-btn>
    </div>

    <router-link
      v-if="!mdAndDown && isAdminPage"
      aria-label="Exit admin settings"
      data-testid="exit-admin-settings-link"
      to="/"
    >
      <v-btn
        icon
        class="tw:!rounded-e-none tw:!rounded-s-md tw:!text-on-surface tw:!transition-all tw:!duration-500"
        color="background"
        :size="itemSize"
        variant="flat"
      >
        <v-icon icon="mdi-arrow-left" />
      </v-btn>
    </router-link>
  </div>
</template>
